import { useState, useEffect, useCallback, useMemo } from "react";
import api from "../api/client";
import useIsMobile from "../hooks/useIsMobile";
import useAuth from "../hooks/useAuth";
import Navbar from "../components/layout/Navbar";
import MobileNavbar from "../components/layout/MobileNavbar";

function getMonday(d) {
  const dt = new Date(d);
  const day = dt.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  dt.setDate(dt.getDate() + diff);
  return dt;
}

function formatDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(d, n) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + n);
  return dt;
}

function formatShortDate(iso) {
  const d = new Date(iso + "T00:00:00");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[d.getMonth()]} ${d.getDate()}`;
}

function formatFullDate(iso) {
  const d = new Date(iso + "T00:00:00");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

const TYPE_OPTIONS = [
  { value: "all", label: "All FSTs" },
  { value: "ni", label: "New Installation" },
  { value: "migration", label: "Migration" },
];

export default function ScorecardPage() {
  const isMobile = useIsMobile();
  const { user, canViewScorecardAllCompanies } = useAuth();

  const today = useMemo(() => new Date(), []);
  const currentMonday = useMemo(() => getMonday(today), [today]);

  const [weekStart, setWeekStart] = useState(currentMonday);
  const [customRange, setCustomRange] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [expanded, setExpanded] = useState(() => new Set());
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(user?.company_id || null);

  const startDate = customRange ? fromDate : formatDate(weekStart);
  const endDate = customRange ? toDate : formatDate(addDays(weekStart, 6));

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("desc");

  // Fetch companies list on mount
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const { data: companiesList } = await api.get("/reports/companies");
        setCompanies(companiesList);
      } catch (err) {
        console.error("Failed to load companies:", err);
      }
    };
    fetchCompanies();
  }, []);

  // Update selected company when user company_id changes
  useEffect(() => {
    if (user?.company_id) {
      setSelectedCompanyId(user.company_id);
    }
  }, [user?.company_id]);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setError(null);
    try {
      const { data: resp } = await api.get("/reports/scorecard", {
        params: { start_date: startDate, end_date: endDate, type: typeFilter, company_id: selectedCompanyId },
      });
      setData(resp);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load scorecard");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, typeFilter, selectedCompanyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Collapse all when the dataset changes (week / type / range)
  useEffect(() => {
    setExpanded(new Set());
  }, [startDate, endDate, typeFilter]);

  const prevWeek = () => {
    setCustomRange(false);
    setWeekStart(addDays(weekStart, -7));
  };
  const currentWeek = () => {
    setCustomRange(false);
    setWeekStart(currentMonday);
  };
  const applyRange = () => {
    if (fromDate && toDate) setCustomRange(true);
  };
  const resetRange = () => {
    setCustomRange(false);
    setFromDate("");
    setToDate("");
    setWeekStart(currentMonday);
  };

  const toggleRow = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const weekNumber = data?.week_number ?? "";
  const headerRange = data
    ? `${formatFullDate(data.start_date)} - ${formatFullDate(data.end_date)}`
    : "";

  const scoreClass = (s) =>
    s > 0 ? "text-blue-700" : s < 0 ? "text-red-600" : "text-gray-400";

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "fst_name" || key === "job_type" ? "asc" : "desc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!data?.rows) return [];
    const rows = [...data.rows];
    rows.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (sortKey === "fst_name" || sortKey === "job_type") {
        av = (av || "").toLowerCase();
        bv = (bv || "").toLowerCase();
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sortDir === "asc" ? cmp : -cmp;
      }
      return sortDir === "asc" ? (av ?? 0) - (bv ?? 0) : (bv ?? 0) - (av ?? 0);
    });
    return rows;
  }, [data?.rows, sortKey, sortDir]);

  const SortHeader = ({ field, children, className = "", ...rest }) => (
    <th
      className={`${className} cursor-pointer select-none hover:bg-blue-800 transition-colors`}
      onClick={() => handleSort(field)}
      {...rest}
    >
      {children}
      {sortKey === field && (
        <span className="ml-1 text-blue-200">{sortDir === "asc" ? "▲" : "▼"}</span>
      )}
    </th>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {isMobile ? <MobileNavbar /> : <Navbar />}

      <div className="flex-1 max-w-[1200px] mx-auto w-full px-4 lg:px-6 py-6">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-gray-800">Scorecard</h1>
          <p className="text-sm text-gray-500">
            Weekly scorecard of individual FST/Team performance based on Done, Cancelled, Rescheduled, and Attachment Compliance.
          </p>
        </div>

        {/* Week header */}
        <div className="text-center mb-4">
          <p className="text-base font-semibold text-blue-700">
            {customRange ? "Custom Range" : `Week ${weekNumber}`}
          </p>
          <p className="text-sm text-gray-500">{headerRange}</p>
        </div>

        {/* Controls row: filter + week navigation */}
        {/* Tabs: FST type filter */}
        <div className="flex justify-center mb-4">
          <div
            role="tablist"
            aria-label="FST type"
            className="inline-flex items-center gap-1 p-1 bg-gray-100 rounded-lg border border-gray-200 shadow-sm"
          >
            {TYPE_OPTIONS.map((o) => {
              const active = typeFilter === o.value;
              return (
                <button
                  key={o.value}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTypeFilter(o.value)}
                  className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    active
                      ? "bg-white text-blue-700 shadow-sm border border-gray-200"
                      : "text-gray-600 hover:text-gray-900 hover:bg-white/60"
                  }`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Controls row: company filter + week navigation */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
          {canViewScorecardAllCompanies && (
            <select
              value={selectedCompanyId || ""}
              onChange={(e) => setSelectedCompanyId(e.target.value ? parseInt(e.target.value) : null)}
              className="px-3 py-2 text-sm font-medium rounded border bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}

          {!customRange && (
            <div className="flex items-center gap-2">
              <button
                onClick={prevWeek}
                className="px-4 py-2 text-sm font-medium rounded bg-gray-200 hover:bg-gray-300 text-gray-700"
              >
                &larr; Prev
              </button>
              <button
                onClick={currentWeek}
                className="px-4 py-2 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Current
              </button>
            </div>
          )}
        </div>

        {/* Custom date range */}
        <details className="mb-4 bg-white rounded-lg shadow-sm border max-w-2xl mx-auto">
          <summary className="px-4 py-2 text-sm font-medium text-gray-600 cursor-pointer select-none">
            Custom Date Range
          </summary>
          <div className="px-4 pb-4 pt-1 space-y-3">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[150px]">
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                />
              </div>
              <div className="flex-1 min-w-[150px]">
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                />
              </div>
              <button
                onClick={applyRange}
                disabled={!fromDate || !toDate}
                className="px-4 py-1.5 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Apply
              </button>
              <button
                onClick={resetRange}
                className="px-4 py-1.5 text-sm font-medium rounded bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Reset
              </button>
            </div>
          </div>
        </details>

        {/* Loading / Error */}
        {loading && (
          <div className="text-center py-8 text-sm text-gray-400">Loading...</div>
        )}
        {error && (
          <div className="text-center py-4 text-sm text-red-600 bg-red-50 rounded-lg">{error}</div>
        )}

        {/* Table */}
        {data && !loading && (
          <div className="bg-white rounded-lg shadow-sm border overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-700 text-white text-xs uppercase tracking-wide">
                  <th className="px-3 py-2 text-center font-semibold w-10"></th>
                  <th className="px-3 py-2 text-center font-semibold w-12">#</th>
                  <SortHeader field="fst_name" className="px-4 py-2 text-left font-semibold whitespace-nowrap" style={{ width: "1%" }}>FST Name</SortHeader>
                  {!isMobile && <SortHeader field="job_type" className="px-2 py-2 text-left font-semibold whitespace-nowrap" style={{ width: "1%" }}>Type</SortHeader>}
                  <SortHeader field="completed" className="px-4 py-2 text-center font-semibold w-20">Done</SortHeader>
                  <SortHeader field="cancelled" className="px-4 py-2 text-center font-semibold w-20">Cancel</SortHeader>
                  <SortHeader field="rescheduled" className="px-4 py-2 text-center font-semibold w-24">Resched</SortHeader>
                  <SortHeader field="att" className="px-4 py-2 text-center font-semibold w-20" title="Attachment Compliance — manual deduction">Att</SortHeader>
                  <SortHeader field="score" className="px-4 py-2 text-center font-semibold w-24">Score</SortHeader>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr>
                    <td colSpan={isMobile ? 8 : 9} className="px-3 py-8 text-center text-sm text-gray-400">
                      No FST output for this period.
                    </td>
                  </tr>
                )}
                {sortedRows.map((row, i) => {
                  const isOpen = expanded.has(row.fst_id);
                  return (
                    <FstRow
                      key={row.fst_id}
                      row={row}
                      rank={i + 1}
                      isOpen={isOpen}
                      onToggle={() => toggleRow(row.fst_id)}
                      striped={i % 2 === 1}
                      scoreClass={scoreClass}
                      today={today}
                      canEditAtt={!!data.can_edit_att}
                      onAttUpdated={fetchData}
                      isMobile={isMobile}
                    />
                  );
                })}
              </tbody>
              {data.rows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-blue-200 bg-blue-50">
                    <td></td>
                    <td className="px-3 py-2 text-sm font-bold text-gray-800" colSpan={2}>Total</td>
                    {!isMobile && <td></td>}
                    <td className="px-4 py-2 text-center text-sm font-bold text-green-700">{data.total.completed}</td>
                    <td className="px-4 py-2 text-center text-sm font-bold text-red-600">
                      {data.total.cancelled}
                      {data.total.cancelled_raw != null && data.total.cancelled_raw !== data.total.cancelled && (
                        <span className="text-xs text-gray-400 ml-0.5">({data.total.cancelled_raw})</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-center text-sm font-bold text-amber-600">
                      {data.total.rescheduled}
                      {data.total.rescheduled_raw != null && data.total.rescheduled_raw !== data.total.rescheduled && (
                        <span className="text-xs text-gray-400 ml-0.5">({data.total.rescheduled_raw})</span>
                      )}
                    </td>
                    <td className={`px-4 py-2 text-center text-sm font-bold ${data.total.att > 0 ? "text-red-600" : "text-gray-400"}`}>{data.total.att ?? 0}</td>
                    <td className={`px-4 py-2 text-center text-sm font-bold ${scoreClass(data.total.score)}`}>
                      {data.total.score}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function FstRow({ row, rank, isOpen, onToggle, striped, scoreClass, today, canEditAtt, onAttUpdated, isMobile }) {
  const todayIso = formatDate(today);
  return (
    <>
      <tr
        onClick={onToggle}
        className={`border-t cursor-pointer ${striped ? "bg-gray-50" : "bg-white"} hover:bg-blue-50`}
      >
        <td className="px-3 py-2 text-center">
          <span
            className={`inline-block transition-transform text-gray-500 ${isOpen ? "rotate-90" : ""}`}
            aria-hidden="true"
          >
            &#9656;
          </span>
        </td>
        <td className="px-3 py-2 text-center text-sm text-gray-700">{rank}</td>
        <td className="px-4 py-2 text-sm text-gray-800 whitespace-nowrap">{row.display_name}</td>
        {!isMobile && (
          <td className="px-2 py-2 text-left whitespace-nowrap">
            <span className="text-xs font-medium text-gray-600">{row.job_type || ""}</span>
          </td>
        )}
        <td className="px-4 py-2 text-center">
          <span className={`text-sm font-semibold ${row.completed > 0 ? "text-green-700" : "text-gray-400"}`}>
            {row.completed}
          </span>
        </td>
        <td className="px-4 py-2 text-center">
          <span className={`text-sm font-semibold ${row.cancelled > 0 ? "text-red-600" : "text-gray-400"}`}>
            {row.cancelled}
            {row.cancelled_raw != null && row.cancelled_raw !== row.cancelled && (
              <span className="text-xs text-gray-400 ml-0.5">({row.cancelled_raw})</span>
            )}
          </span>
        </td>
        <td className="px-4 py-2 text-center">
          <span className={`text-sm font-semibold ${row.rescheduled > 0 ? "text-amber-600" : "text-gray-400"}`}>
            {row.rescheduled}
            {row.rescheduled_raw != null && row.rescheduled_raw !== row.rescheduled && (
              <span className="text-xs text-gray-400 ml-0.5">({row.rescheduled_raw})</span>
            )}
          </span>
        </td>
        <td className="px-4 py-2 text-center" title="Attachment Compliance — sum of daily Att values">
          <span className={`text-sm font-semibold ${(row.att ?? 0) > 0 ? "text-red-600" : "text-gray-400"}`}>
            {row.att ?? 0}
          </span>
        </td>
        <td className="px-4 py-2 text-center">
          <span className={`text-sm font-bold ${scoreClass(row.score)}`}>{row.score}</span>
        </td>
      </tr>
      {isOpen && (
        <tr className="bg-blue-50/40 border-t border-blue-100">
          <td></td>
          <td colSpan={isMobile ? 7 : 8} className="px-4 py-3">
            <div className="overflow-x-auto">
              <table className="w-full text-xs border rounded bg-white">
                <thead>
                  <tr className="bg-blue-100 text-blue-900">
                    <th className="px-2 py-1.5 text-left font-semibold">Day</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Date</th>
                    <th className="px-2 py-1.5 text-center font-semibold">Done</th>
                    <th className="px-2 py-1.5 text-center font-semibold">Cancel</th>
                    <th className="px-2 py-1.5 text-center font-semibold">Resched</th>
                    <th className="px-2 py-1.5 text-center font-semibold" title="Attachment Compliance — manual deduction">Att</th>
                    <th className="px-2 py-1.5 text-center font-semibold">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {(row.daily || []).map((d, i) => {
                    const isToday = d.date === todayIso;
                    const isDayOff = d.is_day_off;
                    return (
                      <tr
                        key={d.date}
                        className={`border-t ${isDayOff ? "bg-yellow-50" : isToday ? "bg-blue-50" : i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
                      >
                        <td className={`px-2 py-1.5 ${isToday ? "font-bold text-blue-700" : "text-gray-600"}`}>
                          {d.day}
                          {isDayOff && <span className="ml-1 text-[10px] text-yellow-600 font-medium">(OFF)</span>}
                        </td>
                        <td className={`px-2 py-1.5 ${isToday ? "font-bold text-blue-700" : "text-gray-700"}`}>{formatShortDate(d.date)}</td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`font-semibold ${d.completed > 0 ? "text-green-700" : "text-gray-400"}`}>{d.completed}</span>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`font-semibold ${d.cancelled > 0 ? "text-red-600" : "text-gray-400"}`}>
                            {d.cancelled}
                            {d.cancelled_raw != null && d.cancelled_raw !== d.cancelled && (
                              <span className="text-[10px] text-gray-400 ml-0.5">({d.cancelled_raw})</span>
                            )}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`font-semibold ${d.rescheduled > 0 ? "text-amber-600" : "text-gray-400"}`}>
                            {d.rescheduled}
                            {d.rescheduled_raw != null && d.rescheduled_raw !== d.rescheduled && (
                              <span className="text-[10px] text-gray-400 ml-0.5">({d.rescheduled_raw})</span>
                            )}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <AttCell
                            value={d.att ?? 0}
                            userId={row.fst_id}
                            date={d.date}
                            canEdit={canEditAtt}
                            onSaved={onAttUpdated}
                          />
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`font-bold ${scoreClass(d.score)}`}>{d.score}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-blue-200 bg-blue-50">
                    <td className="px-2 py-1.5 font-bold text-gray-800" colSpan={2}>Total</td>
                    <td className="px-2 py-1.5 text-center font-bold text-green-700">{row.completed}</td>
                    <td className="px-2 py-1.5 text-center font-bold text-red-600">
                      {row.cancelled}
                      {row.cancelled_raw != null && row.cancelled_raw !== row.cancelled && (
                        <span className="text-[10px] text-gray-400 ml-0.5">({row.cancelled_raw})</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-center font-bold text-amber-600">
                      {row.rescheduled}
                      {row.rescheduled_raw != null && row.rescheduled_raw !== row.rescheduled && (
                        <span className="text-[10px] text-gray-400 ml-0.5">({row.rescheduled_raw})</span>
                      )}
                    </td>
                    <td className={`px-2 py-1.5 text-center font-bold ${(row.att ?? 0) > 0 ? "text-red-600" : "text-gray-400"}`}>{row.att ?? 0}</td>
                    <td className={`px-2 py-1.5 text-center font-bold ${scoreClass(row.score)}`}>{row.score}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function AttCell({ value, userId, date, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? 0));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(String(value ?? 0));
  }, [value]);

  if (!canEdit) {
    return (
      <span className={`font-semibold ${value > 0 ? "text-red-600" : "text-gray-400"}`}>
        {value}
      </span>
    );
  }

  const commit = async () => {
    const n = Math.max(0, parseInt(draft, 10) || 0);
    setEditing(false);
    if (n === value) return;
    setSaving(true);
    try {
      await api.put("/reports/attachment-score", {
        user_id: userId,
        date,
        value: n,
      });
      onSaved && onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save Att");
      setDraft(String(value ?? 0));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <input
        type="number"
        min="0"
        autoFocus
        disabled={saving}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          else if (e.key === "Escape") {
            setDraft(String(value ?? 0));
            setEditing(false);
          }
        }}
        className="w-14 px-1 py-0.5 text-xs text-center border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
      }}
      className={`font-semibold px-2 py-0.5 rounded hover:bg-blue-100 ${value > 0 ? "text-red-600" : "text-gray-400"}`}
      title="Click to edit"
    >
      {value}
    </button>
  );
}
